/**
 * Delete a grupo — the members lose what it granted, and it says so.
 *
 * `window.confirm` cannot name the people. This dialog fetches the
 * group's members first and states the count, because deleting a group
 * is a revocation for everybody in it and the number is the decision.
 *
 * Destructive confirmations use `ConfirmModal` from the kit rather than
 * `window.confirm`: the four `window.confirm` calls on the pages this
 * slice replaces are the grammar being retired.
 */

import { ConfirmModal } from "@omnitok/ui";
import { useCallback, useEffect, useState } from "react";

import { useT } from "@/i18n";
import { groupsApi, type GroupSummaryOut } from "@/lib/access-api";

export function GroupDeleteModal({
  group,
  onClose,
  onDeleted,
}: {
  group: GroupSummaryOut | null;
  onClose: () => void;
  onDeleted: () => void;
}): JSX.Element | null {
  const { t } = useT();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const groupId = group?.id ?? null;

  const reset = useCallback((): void => setError(null), []);
  useEffect(() => {
    reset();
  }, [groupId, reset]);

  if (group === null) return null;

  const submit = async (): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      await groupsApi.remove(group.id);
      onDeleted();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <ConfirmModal
      open
      onClose={onClose}
      title={t("access.groups.delete.title", { name: group.name })}
      confirmText={t("access.groups.delete.confirm")}
      cancelText={t("common.cancel")}
      confirmVariant="danger"
      loading={busy}
      onConfirm={() => void submit()}
      message={
        <div className="space-y-2">
          <p className="text-sm text-neutral-700">
            {t("access.groups.delete.body", {
              members: group.member_count,
              reports: group.entitled_report_count,
            })}
          </p>
          <p className="text-[13px] text-neutral-600">
            {t("access.groups.delete.scopeNote")}
          </p>
          {error !== null && (
            <p className="whitespace-pre-wrap text-[13px] text-error-dark">
              {error}
            </p>
          )}
        </div>
      }
    />
  );
}
